import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Inject,
  Signal,
  viewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogClose,
  MatDialogRef,
} from '@angular/material/dialog';
import { EnableEscCloseDirective } from 'src/app/shared/directives/enable-esc-close.directive';

import {
  FlowNodeTypes,
  NODE_EDIT_TAB_COMPONENT,
  NodeEditDialogOutput,
  NodeEditTab,
  SelectedNodeForEdit,
} from '../../flow-chart.model';
import { EditWebhookComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-webhook/edit-webhook.component';
import { EditConditionComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-condition/edit-condition.component';
import { PreloaderColor, UiButtonComponent, UiPreloaderComponent } from '@ui';
import { EditWaitWebhook } from 'src/app/pages/workflow/components/node-edit-tabs/edit-wait-webhook/edit-wait-webhook';
import { EditHttpRequestComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-http-request/edit-http-request.component';
import { EditEchoComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-echo/edit-echo.component';
import { EditAlertsComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-alerts/edit-alerts.component';
import { Store } from '@ngrx/store';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { EditWaitAlertComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-wait-alert/edit-wait-alert.component';
import { EditSendEmailComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-send-email/edit-send-email.component';
import { EditSendSmsComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-send-sms/edit-send-sms.component';
import { EditWaitSmsComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-wait-sms/edit-wait-sms.component';
import { EditWaitEmailComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-wait-email/edit-wait-email.component';
import { WorkflowModel } from '@models/workflow.model';
import { EditWaitComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-wait/edit-wait.component';
import { EditMobilePushComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-mobile-push/edit-mobile-push.component';
import { EditWaitMobilePushlComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-wait-mobile-push/edit-wait-mobile-push.component';
import { EditVideoComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-video/edit-video.component';
import { EditSetFpsComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-set-fps/edit-set-fps.component';
import { EditTrimComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-trim/edit-trim.component';
import { EditCropComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-crop/edit-crop.component';
import { EditResizeComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-resize/edit-resize.component';
import { EditGetImageComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-get-image/edit-get-image.component';
import { EditAIComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-ai/edit-ai.component';
import { EditTriggerManuallyComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-trigger-manually/edit-trigger-manually.component';
import { EditScheduleTriggerComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-schedule-trigger/edit-schedule-trigger.component';
import { EditJsCodeComponent } from 'src/app/pages/workflow/components/node-edit-tabs/edit-js-code/edit-js-code.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-node-edit-modal',
  imports: [
    MatDialogClose,
    UiButtonComponent,
    UiPreloaderComponent,
    EditWebhookComponent,
    EditConditionComponent,
    EditWaitWebhook,
    EditHttpRequestComponent,
    EditEchoComponent,
    EditAlertsComponent,
    EditWaitAlertComponent,
    EditSendEmailComponent,
    EditSendSmsComponent,
    EditWaitSmsComponent,
    EditWaitEmailComponent,
    EditWaitComponent,
    EditMobilePushComponent,
    EditWaitMobilePushlComponent,
    EditVideoComponent,
    EditSetFpsComponent,
    EditTrimComponent,
    EditCropComponent,
    EditResizeComponent,
    EditGetImageComponent,
    EditAIComponent,
    EditTriggerManuallyComponent,
    EditScheduleTriggerComponent,
    EditJsCodeComponent,
    TranslatePipe,
  ],
  templateUrl: './node-edit-modal.component.html',
  styleUrl: './node-edit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [EnableEscCloseDirective],
})
export class NodeEditModalComponent implements AfterViewInit {
  public loaderColor = PreloaderColor;
  
  constructor(
    private dialogRef: MatDialogRef<
      NodeEditModalComponent,
      NodeEditDialogOutput
    >,
    @Inject(MAT_DIALOG_DATA) public data: SelectedNodeForEdit<any>,
    private store$: Store,
  ) {}

  protected readonly workflowNodeTypes = WorkflowModel.WorkflowNodeTypes;
  protected readonly flowNodeTypes = FlowNodeTypes;

  protected readonly activeEditTabSig = viewChild<NodeEditTab>(
    NODE_EDIT_TAB_COMPONENT,
  );

  public selectIsNodeLoadingSig: Signal<boolean> =
    this.store$.selectSignal(WorkflowsSelectors.selectIsNodeLoading);

  public ngAfterViewInit(): void {
    if (!this.selectIsNodeLoadingSig() && this.data?.data?.shouldMarkFormAsTouched) {
      this.activeEditTabSig()?.form?.markAllAsTouched();
    }
  }

  public close(): void {
    this.dialogRef.close();
  }

  public save(): void {
    if (!this.activeEditTabSig()) {
      return;
    }

    if (!this.activeEditTabSig().form) {
      this.dialogRef.close();
      return;
    }

    const value = this.activeEditTabSig().form.value as Record<string, unknown>;

    const modalData: NodeEditDialogOutput = {
      id: this.data?.id,
      data: value,
    };

    this.dialogRef.close(modalData);
  }
}
